import { SurveyAnswers } from "../../common/hooks/api/useSurvey";
import { OrganizationDataRecipientDto } from "../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationManagedAdNetworkDto } from "../../common/service/server/dto/OrganizationManagedAdNetworkDto";
import { CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG } from "../surveys/sharingSurvey";
import { parseMultiselectAnswerWithNoneOption } from "../surveys/survey";
import { VENDOR_KEY_TRUEVAULT_CONNECTED_SYSTEM } from "../types/Vendor";

/**
 * Single source of truth for "vendor nesting": a data recipient that handles requests on behalf of other data
 * recipients, so the nested ones are hidden from request workflows and listed underneath their manager instead.
 *
 * There are three sources of nesting, all stored as rows in `organization_managed_ad_network`:
 *
 *  1. Ad networks whose audience lists are managed by an agency. These only nest while the manager is still selected
 *     in the `custom-audience-feature-separate-vendor` question of the selling & sharing survey - deselecting the
 *     manager there has always un-nested its networks without deleting the rows.
 *  2. Systems automated through TrueVault Connected System (POL-7958). TVCS is added by a TrueVault admin and is not
 *     part of any survey, so its relations are always active.
 *  3. "Data retrieved via" links (DES-43): the nested recipient's data is pulled from another recipient, so Access and
 *     Portability list it under that recipient instead of asking for its own outcome. These are Access-only and come
 *     from a separate endpoint - only the Access callers pass them, via `options.dataRetrievedVia`.
 *
 * Callers pass the vendor list they are already working with; a manager only counts as active when it appears in that
 * list, which matches the pre-existing per-step behavior.
 */
export type VendorNesting = {
  /** Data recipient ids of managers whose nesting is currently in effect. */
  activeManagerIds: string[];
  /** The active managers themselves, as found in the vendor list passed to `getVendorNesting`. */
  activeManagers: OrganizationDataRecipientDto[];
  /** Data recipient ids nested under an active manager. These are hidden from workflow and setup lists. */
  managedVendorIds: string[];
  /** Managers selected in the custom-audience survey question, whether or not they manage anything. */
  customAudienceManagerIds: string[];
  isManaged: (dataRecipientId: string | undefined) => boolean;
  isActiveManager: (dataRecipientId: string | undefined) => boolean;
  getManagersForManagedVendor: (
    dataRecipientId: string | undefined,
  ) => OrganizationDataRecipientDto[];
  /** First manager of a nested vendor, for the single-manager displays. */
  getManagerForManagedVendor: (
    dataRecipientId: string | undefined,
  ) => OrganizationDataRecipientDto | null;
  /** Recipients the manager handles requests for (sources 1 and 2). */
  getManagedVendorsForManager: (
    dataRecipientId: string | undefined,
  ) => OrganizationDataRecipientDto[];
  /** Recipients whose data is retrieved via this one (source 3). Always empty unless `dataRetrievedVia` was passed. */
  getRetrievedViaVendorsForManager: (
    dataRecipientId: string | undefined,
  ) => OrganizationDataRecipientDto[];
};

const EMPTY_NESTING: VendorNesting = {
  activeManagerIds: [],
  activeManagers: [],
  managedVendorIds: [],
  customAudienceManagerIds: [],
  isManaged: () => false,
  isActiveManager: () => false,
  getManagersForManagedVendor: () => [],
  getManagerForManagedVendor: () => null,
  getManagedVendorsForManager: () => [],
  getRetrievedViaVendorsForManager: () => [],
};

export const isConnectedSystemVendor = (
  vendor: Pick<OrganizationDataRecipientDto, "vendorKey"> | null | undefined,
): boolean => vendor?.vendorKey === VENDOR_KEY_TRUEVAULT_CONNECTED_SYSTEM;

type GetVendorNestingOptions = {
  /**
   * Restricts which recipients may act as a manager. Used by the opt-out lists, which exclude some vendors (e.g.
   * Google Analytics) from the workflow entirely and so must not surface them as a manager row either.
   */
  isManagerEligible?: (vendor: OrganizationDataRecipientDto) => boolean;
  /**
   * Restricts nesting to the recipients the caller would otherwise have shown itself. Each workflow step passes its
   * own row filter, so a recipient is listed under its manager exactly where it would have appeared on its own, and
   * is never pulled into a workflow it has no part in. A nested recipient that fails this check is not hidden
   * either, so it keeps whatever treatment it would have had without a manager.
   */
  isNestedVendorRelevant?: (vendor: OrganizationDataRecipientDto) => boolean;
  /**
   * Access-only "Data retrieved via" links (DES-43). Pass these only from the Access and Portability workflow and its
   * setup pages; every other workflow must keep listing the linked recipient on its own.
   */
  dataRetrievedVia?: OrganizationManagedAdNetworkDto[] | null;
  /**
   * Restricts which recipients a "Data retrieved via" link may point at. The request step passes the request's context
   * and region, so an HR request never answers for a consumer-only vendor in place of the one it links.
   */
  isDataRetrievedViaEligible?: (vendor: OrganizationDataRecipientDto) => boolean;
};

export const getVendorNesting = (
  allVendors: OrganizationDataRecipientDto[] | null | undefined,
  managedAdNetworks: OrganizationManagedAdNetworkDto[] | null | undefined,
  sellingAndSharingAnswers: SurveyAnswers,
  options: GetVendorNestingOptions = {},
): VendorNesting => {
  if (!allVendors || !managedAdNetworks) {
    return EMPTY_NESTING;
  }

  const customAudienceManagerIds = parseMultiselectAnswerWithNoneOption(
    sellingAndSharingAnswers,
    CUSTOM_AUDIENCE_VENDOR_FEATURE_SLUG,
  );

  const vendorsById = new Map(allVendors.map((vendor) => [vendor.id, vendor]));
  const isManagerEligible = options.isManagerEligible ?? (() => true);
  const isNestedVendorRelevant = options.isNestedVendorRelevant ?? (() => true);

  // A relation is active when TVCS manages it, or when its ad-network manager is still selected in the survey.
  const activeRelations = managedAdNetworks.filter(
    (relation) =>
      isConnectedSystemVendor(vendorsById.get(relation.managerId)) ||
      relation.managerVendorKey === VENDOR_KEY_TRUEVAULT_CONNECTED_SYSTEM ||
      customAudienceManagerIds.includes(relation.managerId),
  );

  const eligibleManagerIds = new Set(
    [...new Set(activeRelations.map((relation) => relation.managerId))]
      .map((managerId) => vendorsById.get(managerId))
      .filter((manager): manager is OrganizationDataRecipientDto => !!manager)
      .filter(isManagerEligible)
      .map((manager) => manager.id),
  );

  // A relation only takes effect when both ends belong in this list: the manager must be present (otherwise the
  // nested vendor would disappear with nothing shown in its place) and the nested vendor must be one this list
  // would have shown (otherwise an unrelated recipient is listed under the manager).
  const effectiveRelations = activeRelations.filter((relation) => {
    if (!eligibleManagerIds.has(relation.managerId)) {
      return false;
    }
    const nested = vendorsById.get(relation.networkId);
    return !!nested && isNestedVendorRelevant(nested);
  });

  const managedRelations = effectiveRelations;
  const managedNestedIds = new Set(managedRelations.map((relation) => relation.networkId));
  const managedManagerIds = new Set(managedRelations.map((relation) => relation.managerId));
  const isDataRetrievedViaEligible = options.isDataRetrievedViaEligible ?? (() => true);

  // A "Data retrieved via" link applies under the same two-ended rule, and only between recipients that would each
  // have a row of their own here: a recipient another manager already handles stays under that manager, a link to a
  // recipient that is itself nested is ignored (so the linked recipient never disappears into a hidden row), and a
  // recipient that handles requests for others keeps its own row instead of being listed twice.
  const retrievedViaRelations = (options.dataRetrievedVia ?? []).filter((relation) => {
    const manager = vendorsById.get(relation.managerId);
    const nested = vendorsById.get(relation.networkId);
    return (
      !!manager &&
      !!nested &&
      isManagerEligible(manager) &&
      isDataRetrievedViaEligible(manager) &&
      isNestedVendorRelevant(nested) &&
      !isConnectedSystemVendor(manager) &&
      !isConnectedSystemVendor(nested) &&
      !managedNestedIds.has(relation.managerId) &&
      !managedNestedIds.has(relation.networkId) &&
      !managedManagerIds.has(relation.networkId)
    );
  });
  const allEffectiveRelations = managedRelations.concat(retrievedViaRelations);

  const managedVendorIds = [
    ...new Set(allEffectiveRelations.map((relation) => relation.networkId)),
  ];
  const managedVendorIdSet = new Set(managedVendorIds);

  // A manager is only "active" here when it actually manages something in this list, so callers can add it as a
  // row - and render its "Vendors managed by ..." list - without ever producing an empty one.
  const activeManagers = [...new Set(allEffectiveRelations.map((relation) => relation.managerId))]
    .map((managerId) => vendorsById.get(managerId))
    .filter((manager): manager is OrganizationDataRecipientDto => !!manager);
  const activeManagerIdSet = new Set(activeManagers.map((manager) => manager.id));

  // The same pair can be stored twice (the setup UI can double-submit), so resolve to distinct recipients - a
  // duplicate relation would otherwise render the recipient twice under its manager.
  const resolveDistinct = (
    relations: OrganizationManagedAdNetworkDto[],
    pickId: (relation: OrganizationManagedAdNetworkDto) => string,
  ) =>
    [...new Set(relations.map(pickId))]
      .map((id) => vendorsById.get(id))
      .filter((vendor): vendor is OrganizationDataRecipientDto => !!vendor);

  const getManagersForManagedVendor = (dataRecipientId: string | undefined) =>
    resolveDistinct(
      allEffectiveRelations.filter((relation) => relation.networkId === dataRecipientId),
      (relation) => relation.managerId,
    );

  const getManagedVendorsForManager = (dataRecipientId: string | undefined) =>
    resolveDistinct(
      managedRelations.filter((relation) => relation.managerId === dataRecipientId),
      (relation) => relation.networkId,
    );

  const getRetrievedViaVendorsForManager = (dataRecipientId: string | undefined) =>
    resolveDistinct(
      retrievedViaRelations.filter((relation) => relation.managerId === dataRecipientId),
      (relation) => relation.networkId,
    );

  return {
    activeManagerIds: activeManagers.map((manager) => manager.id),
    activeManagers,
    managedVendorIds,
    customAudienceManagerIds,
    isManaged: (dataRecipientId: string | undefined) => managedVendorIdSet.has(dataRecipientId),
    isActiveManager: (dataRecipientId: string | undefined) =>
      activeManagerIdSet.has(dataRecipientId),
    getManagersForManagedVendor,
    getManagerForManagedVendor: (dataRecipientId: string | undefined) =>
      getManagersForManagedVendor(dataRecipientId)[0] || null,
    getManagedVendorsForManager,
    getRetrievedViaVendorsForManager,
  };
};
